import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './components/Sidebar.js';
import About from './components/About.js';
import Experience from './components/Experience.js';
import Education from './components/Education.js';
import Patents from './components/Patents.js';
import MainMediaGallery from './components/MainMediaGallery.js';
import Modal from './components/Modal.js';
import SkillModal from './components/SkillModal.js';
import ParticleBackground from './components/ParticleBackground.js';
import ThemeToggle from './components/ThemeToggle.js';
import ProtectedAdmin from './components/ProtectedAdmin.js';
import CvManagementPage from './components/CvManagementPage.js';
import { AuthProvider } from './context/AuthContext.js';
import { CvDataProvider, useCvData } from './context/CvDataContext.js';
import './App.css';

// Main app content component that uses CV data
function MainAppContent() {
  const { mainMedia, loading, error, reload } = useCvData();
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTopFab, setShowTopFab] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);


  const handleExperienceClick = (experience) => {
    setSelectedExperience(experience);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExperience(null);
  };

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    setIsSkillModalOpen(true);
  };

  const handleCloseSkillModal = () => {
    setIsSkillModalOpen(false);
    setSelectedSkill(null);
  };

  const handleCompanyClickFromSkill = (experience) => {
    if (experience) {
      // Close skill modal and open experience modal
      setIsSkillModalOpen(false);
      setSelectedSkill(null);
      setSelectedExperience(experience);
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const atTop = window.scrollY <= 80;
        setShowTopFab(!atTop && !isModalOpen && !isSkillModalOpen);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isModalOpen, isSkillModalOpen]);

  return (
    <div className="App">
      <ThemeToggle />
      <ParticleBackground />
      <div className="container">
        <Sidebar onSkillClick={handleSkillClick} />
        <main className="main-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <About />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Experience onExperienceClick={handleExperienceClick} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Education />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Patents />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              {error ? (
                <section id="media" className="media-section">
                  <h2>Media Gallery</h2>
                  <p role="alert">{error.message || 'Failed to load media.'}</p>
                  <button onClick={reload} className="retry-btn">Retry</button>
                </section>
              ) : loading ? (
                <section id="media" className="media-section">
                  <h2>Media Gallery</h2>
                  <div className="media-grid">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="media-item">
                        <div className="skeleton skeleton-rect" style={{ height: 180 }} />
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
                <MainMediaGallery mediaData={mainMedia} />
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>

      <Modal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        experience={selectedExperience}
      />

      <SkillModal 
        isOpen={isSkillModalOpen}
        onClose={handleCloseSkillModal}
        technology={selectedSkill}
        onCompanyClick={handleCompanyClickFromSkill}
      />

      {/* Floating scroll-to-top button (mobile) */}
      <motion.button
        type="button"
        className={`fab-top ${showTopFab ? 'visible' : ''}`}
        aria-label="Scroll to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: showTopFab ? 1 : 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <motion.i 
          className="fas fa-arrow-up" 
          aria-hidden
          animate={{ y: [0, -2, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        ></motion.i>
      </motion.button>

      {/* Mobile hamburger to jump between sections (hidden when modal open) */}
      {!isModalOpen && !isSkillModalOpen && (
        <>
          <motion.button
            type="button"
            className={`fab-menu ${menuOpen ? 'active' : ''}`}
            aria-label="Open navigation menu"
            onClick={() => setMenuOpen((v) => !v)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            animate={{ rotate: menuOpen ? 90 : 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <motion.i 
              className="fas fa-bars" 
              aria-hidden
              animate={{ rotate: menuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            ></motion.i>
          </motion.button>
          <AnimatePresence>
            {menuOpen && (
              <motion.nav 
                className="mobile-menu" 
                aria-label="Section navigation"
                initial={{ opacity: 0, scale: 0.8, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <motion.a 
                  href="#about" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-user"></i> About
                </motion.a>
                <motion.a 
                  href="#experience" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-briefcase"></i> Experience
                </motion.a>
                <motion.a 
                  href="#education" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-graduation-cap"></i> Education
                </motion.a>
                <motion.a 
                  href="#patents" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-certificate"></i> Patents
                </motion.a>
                <motion.a 
                  href="#media" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-images"></i> Media
                </motion.a>
                <motion.a 
                  href="?admin=true" 
                  onClick={() => setMenuOpen(false)}
                  whileHover={{ x: 5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="admin-link"
                >
                  <i className="fas fa-cog"></i> Admin
                </motion.a>
              </motion.nav>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Floating close button for modal (fixed to viewport) */}
      {(isModalOpen || isSkillModalOpen) && (
        <button
          type="button"
          className="fab-close"
          aria-label="Close dialog"
          onClick={isModalOpen ? handleCloseModal : handleCloseSkillModal}
        >
          <i className="fas fa-times" aria-hidden></i>
        </button>
      )}
    </div>
  );
}

// Read URL parameters once at module level (available synchronously)
function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    cvName: urlParams.get('name') || 'default',
    showAdmin: urlParams.get('admin') === 'true',
    showCvManagement: urlParams.get('cv-management') === 'true',
  };
}

// Main App component that handles URL parameters and routing
function App() {
  const [{ cvName, showAdmin, showCvManagement }] = useState(getUrlParams);

  // Show CV management page if cv-management=true in URL
  if (showCvManagement) {
    return (
      <AuthProvider>
        <div className="App">
          <CvManagementPage />
        </div>
      </AuthProvider>
    );
  }

  // Show admin panel if admin=true in URL
  if (showAdmin) {
    return (
      <CvDataProvider cvName={cvName}>
        <AuthProvider>
          <div className="App">
            <ProtectedAdmin />
          </div>
        </AuthProvider>
      </CvDataProvider>
    );
  }

  return (
    <CvDataProvider cvName={cvName}>
      <div className="App">
        <MainAppContent />
      </div>
    </CvDataProvider>
  );
}

export default App;
